import {
  AlertTriangle,
  CheckCircle2,
  Info,
  XCircle,
  XIcon,
} from "lucide-react";
import type { MouseEvent, ReactNode } from "react";
import { useCallback } from "react";
import { useTranslation } from "../../i18n";
import { cn } from "../../utils";
import { Button } from "../Button/Button";
import { IconButton } from "../Button/IconButton";
import { Text } from "../Typography";
import type { ToastProps, ToastVariant } from "./types";

const iconMap: Record<ToastVariant, ReactNode> = {
  info: <Info size={20} />,
  success: <CheckCircle2 className="rtl:-scale-x-100" size={20} />,
  warning: <AlertTriangle size={20} />,
  error: <XCircle size={20} />,
};

const iconStyles: Record<ToastVariant, string> = {
  info: "text-neutral-500/50",
  success: "text-success-500",
  warning: "text-warning-500",
  error: "text-danger-500",
};

export const ToastView = ({
  variant = "info",
  title,
  text,
  icon,
  action,
  onClose,
}: ToastProps) => {
  const { t } = useTranslation();
  const iconJSX = icon || iconMap[variant];

  const handleDismiss = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (onClose) {
        onClose();
      }
    },
    [onClose],
  );

  const handleActionClick = useCallback(() => {
    if (onClose) onClose();
    action?.onClick();
  }, [action, onClose]);

  return (
    <div className={cn("h-full flex items-start gap-4 p-4 pe-8")}>
      <div className={cn("flex-shrink-0", iconStyles[variant])}>{iconJSX}</div>
      <div className="flex-1 min-w-0">
        {title && (
          <Text className="text-neutral-500" weight="medium" size="sm">
            {title}
          </Text>
        )}
        {text && (
          <Text className="text-neutral-500/50 mt-1" size="sm">
            {text}
          </Text>
        )}
        {action && (
          <div className="mt-2">
            <Button
              size="sm"
              variant={action.variant ?? "outlined"}
              color={action.color}
              onClick={handleActionClick}
              aria-label={action.ariaLabel}
            >
              {action.label}
            </Button>
          </div>
        )}
      </div>
      <div className="flex-shrink-0">
        <IconButton
          size="sm"
          variant="ghost"
          className="absolute top-2 end-2"
          onClick={handleDismiss}
          content={<XIcon size={16} />}
          aria-label={t("toast.closeNotification")}
        />
      </div>
    </div>
  );
};
